/**
 * Sale terms — the conditions a bidder is agreeing to.
 *
 * A MODAL, NOT A PAGE, and the same component in two mounts: the Sales row
 * (open *or* ended) and a section on the sale-results page. Claude Design,
 * 2026-09-08: an ended sale's terms are the terms its invoices were struck
 * under, so they cannot live only on open rows. The content is short and a
 * bidder scanning a list should not lose the list to read it.
 *
 * EVERY FIGURE HERE IS A STORED FIELD. Nothing is inferred and nothing is
 * illustrative — a bidder reads this before committing money, so a plausible
 * number would be worse than no number. Where the platform holds nothing, the
 * section is absent rather than empty.
 *
 * 🔴 NOTHING DATE-SHAPED FOR COLLECTION. Pickup times are prose the house
 * typed; `auctions.pickup_dates` is the only place an absolute date could live
 * and the auction-creation scheduler that writes it does not exist yet. So
 * there is no countdown, no "collect by", and no empty date row waiting to be
 * filled. Preview dates ARE held fields and do appear.
 * See `memory/reference_pickup_has_no_machine_readable_date.md`.
 *
 * 🔴 NO POSSESSIVE ON THE HOUSE NAME. It is tenant data, and a hardcoded `'s`
 * prints "Discount Tool Auctions's". Sentences are written to avoid needing
 * one rather than special-casing a trailing "s".
 */
import {
  bpsLabel,
  formatCents,
  formatInZone,
  resolveTimeZone,
  type Tables,
} from '@auctisync/shared';
import { secondsLabel } from '@auctisync/shared';
import { parsePickupWindows } from '@auctisync/api-client';
import { AuctionPreviewEvents } from './AuctionPreviewEvents';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Exactly the fields the terms modal reads — no more. Declared as a Pick rather
 * than the whole row so both callers can see what they owe it: the sale-results
 * page passes a full row, and the Sales list selects these columns and no
 * others rather than widening to `*` for a modal most visitors never open.
 */
export type SaleTermsAuction = Pick<
  Tables<'auctions'>,
  | 'id'
  | 'title'
  | 'description'
  | 'lot_count'
  | 'buyers_premium_bps'
  | 'online_premium_bps'
  | 'requires_terms_acceptance'
  | 'requires_bidder_approval'
  | 'requires_cc_preauth'
  | 'cc_preauth_amount_cents'
  | 'extension_threshold_seconds'
  | 'extension_amount_seconds'
  | 'preview_text'
  | 'preview_starts_at'
  | 'preview_ends_at'
  | 'pickup_location'
  | 'pickup_address_line1'
  | 'pickup_address_line2'
  | 'pickup_city'
  | 'pickup_region'
  | 'pickup_postal_code'
  | 'pickup_instructions'
  | 'pickup_dates'
  | 'timezone'
>;

type Auction = SaleTermsAuction;

function addressLines(a: Auction): string[] {
  const street = [a.pickup_address_line1, a.pickup_address_line2].filter(Boolean).join(', ');
  const region = [a.pickup_city, a.pickup_region].filter(Boolean).join(', ');
  const lastLine = [region, a.pickup_postal_code].filter(Boolean).join(' ');
  return [a.pickup_location, street, lastLine].filter((l): l is string => Boolean(l && l.trim()));
}

export function SaleTermsModal({
  auction,
  houseName,
  onClose,
  browseTo,
}: {
  auction: Auction;
  houseName: string;
  onClose: () => void;
  /** Where "Browse lots" goes, when the sale is still open. Null hides it. */
  browseTo: string | null;
}) {
  // The sale's own clock — every time in this modal is read in it and names it.
  const saleZone = resolveTimeZone(auction.timezone);

  /*
   * Collection times the house actually dated, in the sale's zone.
   *
   * Prose in `pickup_instructions` is shown separately and untouched: those are
   * the house's own words and we do not know which clock they meant, so
   * reformatting them would be inventing a fact.
   */
  const collectionTimes = parsePickupWindows(auction.pickup_dates).map((w) => {
    const start = formatInZone(w.startsAt, saleZone);
    const span =
      w.endsAt === null
        ? start
        : `${formatInZone(w.startsAt, saleZone, { dateStyle: 'medium', timeStyle: 'short', withZone: false })} – ${formatInZone(w.endsAt, saleZone, { hour: 'numeric', minute: '2-digit' })}`;
    return w.note === null ? span : `${span} — ${w.note}`;
  });

  // Escape closes. A modal that can only be dismissed by hitting a small ✕ is a
  // trap on a keyboard, and this one can be opened from a list a bidder is
  // moving through quickly.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const bp = auction.buyers_premium_bps;
  const op = auction.online_premium_bps;
  // The worked example is the thing most bidders open this for. $100 is chosen
  // because the arithmetic is legible, and it is computed from the sale's own
  // rates rather than written down.
  const exampleHammer = 10_000;
  const exampleTotal = exampleHammer + Math.round((exampleHammer * (bp + (op ?? 0))) / 10_000);

  const requirements: { title: string; body: string }[] = [
    auction.requires_terms_acceptance
      ? { title: 'You accept these terms', body: 'A tick box on your first bid in this sale.' }
      : null,
    auction.requires_bidder_approval
      ? {
          title: `${houseName} approves you`,
          body: 'The house approves its own bidders before their first bid.',
        }
      : null,
    auction.requires_cc_preauth
      ? {
          title: 'A card on file, verified once',
          body:
            auction.cc_preauth_amount_cents === null
              ? 'A small amount is authorised and released. It is not a charge.'
              : `${formatCents(auction.cc_preauth_amount_cents)} is authorised and released. It is not a charge, and not a hold against what you can bid.`,
        }
      : null,
  ].filter((r): r is { title: string; body: string } => r !== null);

  const address = addressLines(auction);
  const antiSnipe = auction.extension_threshold_seconds > 0 && auction.extension_amount_seconds > 0;
  const hasPreview = Boolean(auction.preview_text) || auction.preview_starts_at !== null;

  return (
    <div
      className="w-modal-wrap"
      role="dialog"
      aria-modal="true"
      aria-label={`Sale terms, ${auction.title}`}
    >
      <div className="w-modal-bg" onClick={onClose} />
      <div className="w-modal wr-terms">
        <div className="wr-terms-h">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="w-lbl" style={{ marginBottom: 6 }}>
              Sale terms
            </div>
            <h2 className="w-h2" style={{ fontSize: 21 }}>
              {auction.title}
            </h2>
          </div>
          <button type="button" className="w-ico" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="wr-terms-b">
          <section className="wr-tsec">
            <h4>What you pay on top</h4>
            <div className="wr-prem">
              <div>
                <span className="w-lbl">Buyer&rsquo;s premium</span>
                <b>{bpsLabel(bp)}</b>
              </div>
              {op !== null ? (
                <div>
                  <span className="w-lbl">Online premium</span>
                  <b>{bpsLabel(op)}</b>
                </div>
              ) : null}
              <div>
                <span className="w-lbl">On a {formatCents(exampleHammer)} hammer</span>
                <b>{formatCents(exampleTotal)}</b>
              </div>
            </div>
            {/* An invoice must reconcile, and per-lot tax is genuinely why.
                `lots.tax_exempt` and per-sale tax profiles mean two lines on
                one invoice can carry different tax. Saying so here stops the
                total on the invoice reading as an error.
                See `memory/reference_an_invoice_must_reconcile.md`. */}
            <p className="w-body muted" style={{ marginTop: 12 }}>
              Tax is applied per lot, and the rate can differ between lots in the same sale. Your
              invoice shows the rate charged on each.
            </p>
          </section>

          {requirements.length > 0 ? (
            <section className="wr-tsec">
              <h4>Before you can bid</h4>
              {requirements.map((r) => (
                <div className="wr-check" key={r.title}>
                  <span className="wr-tick" aria-hidden>
                    ✓
                  </span>
                  <span>
                    <b>{r.title}</b>
                    <span className="muted" style={{ display: 'block', marginTop: 2 }}>
                      {r.body}
                    </span>
                  </span>
                </div>
              ))}
            </section>
          ) : null}

          {/* A real term a bidder is owed, in the words the rule is written in.
              Threshold and amount are stored seconds; the sentence is derived,
              so a house that changes the rule changes this line. */}
          <section className="wr-tsec">
            <h4>How lots close</h4>
            <p className="w-body">
              Lots close in staggered order, not all at once.
              {antiSnipe ? (
                <>
                  {' '}
                  <b>
                    A bid in the last {secondsLabel(auction.extension_threshold_seconds)} extends
                    that lot by {secondsLabel(auction.extension_amount_seconds)}
                  </b>
                  , every time it happens, until the bidding stops. A lot cannot be won by bidding
                  in its last second.
                </>
              ) : null}
            </p>
          </section>

          {/*
           * Scheduled viewing events live in `auction_preview_events` and are
           * the ONE piece of sale scheduling that carries real absolute times —
           * unlike collection, which is prose. They used to render on the
           * auction detail page; that page is now results-only, so without this
           * they would have had no home at all.
           *
           * The component shows future events only, so an ended sale's terms
           * simply do not draw it.
           */}
          <AuctionPreviewEvents auctionId={auction.id} />

          {hasPreview ? (
            <section className="wr-tsec">
              <h4>Preview</h4>
              {auction.preview_text ? (
                <p className="w-body">{auction.preview_text}</p>
              ) : (
                <p className="w-body">
                  Preview opens {formatInZone(auction.preview_starts_at, saleZone)}
                  {auction.preview_ends_at !== null
                    ? ` and closes ${formatInZone(auction.preview_ends_at, saleZone)}`
                    : ''}
                  .
                </p>
              )}
            </section>
          ) : null}

          <section className="wr-tsec">
            <h4>Collection</h4>
            <p className="w-body">
              <b>Collection only. {houseName} does not ship.</b>
            </p>
            {address.length > 0 ? (
              <div className="wr-addr">
                <span aria-hidden style={{ marginTop: 2 }}>
                  📍
                </span>
                <span>
                  {address.map((line) => (
                    <span key={line} style={{ display: 'block' }}>
                      {line}
                    </span>
                  ))}
                </span>
              </div>
            ) : null}
            {/* 🔴 DATED COLLECTION TIMES, WHICH THIS MODAL COULD NOT SHOW UNTIL
                2026-09-18. The comment at the top of this file used to say there
                was "nothing date-shaped for collection" — true while
                `auctions.pickup_dates` had no writer. The auction form writes it
                now, so a house that gave real dates gets them printed, in the
                sale's own zone and named. A house that gave only prose still
                gets its prose and nothing invented. */}
            {collectionTimes.length > 0 ? (
              <ul className="w-body" style={{ margin: '12px 0 0', paddingLeft: 18 }}>
                {collectionTimes.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}
            {auction.pickup_instructions ? (
              <p className="w-body muted" style={{ marginTop: 12 }}>
                {auction.pickup_instructions}
              </p>
            ) : null}
          </section>

          {auction.description ? (
            <section className="wr-tsec">
              <h4>Terms from the house</h4>
              <p className="w-body" style={{ whiteSpace: 'pre-wrap' }}>
                {auction.description}
              </p>
            </section>
          ) : null}
        </div>

        <div className="wr-terms-f">
          <button type="button" className="w-btn w-btn-out" onClick={onClose}>
            Close
          </button>
          {browseTo !== null ? (
            <Link className="w-btn w-btn-cta" to={browseTo} onClick={onClose}>
              Browse {String(auction.lot_count)} lots<span aria-hidden>›</span>
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
